import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { usePrefs } from '@/lib/prefs';
import { RoleBadge } from '@/components/common/RoleBadge';
import { canSeeAudit, canSeeWOs, isOwner, canSeePendingInvoices, canSeeQuotes, canSeeVisits } from '@/lib/roles';
import { useFeature } from '@/lib/features';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useSyncHealth } from '@/lib/useAutoRefetch';
import { Logo } from '@/components/brand/Logo';
import {
  Home, Layers, Inbox, BookOpen, FileText, Mail, Compass, Bell,
  Users, ShieldAlert, ShieldCheck, LogOut, PanelLeftClose, PanelLeftOpen, X, Settings, Star, ReceiptText, Wrench, ClipboardList, Plane, FolderTree, Target, Clock, Archive, AlertTriangle, CalendarDays, Database, BarChart3, GraduationCap, MessageSquare, ClipboardCheck, Briefcase, Archive as ArchiveIcon, Link as LinkIcon, CheckSquare
} from 'lucide-react';

const NavItem = ({ to, icon: Icon, label, testId, end = false, collapsed, badge = 0 }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    title={collapsed ? label : undefined}
    style={{ position: 'relative' }}
    className={({ isActive }) => `side-nav-item ${isActive ? 'active' : ''}`}
  >
    <Icon size={16} strokeWidth={1.8} />
    {!collapsed && <span className="truncate flex-1">{label}</span>}
    {badge > 0 && !collapsed && (
      <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white flex-none"
            style={{ background: 'hsl(var(--danger))' }}
            data-testid={`${testId}-badge`}>{badge > 99 ? '99+' : badge}</span>
    )}
    {badge > 0 && collapsed && (
      <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: 'hsl(var(--danger))' }} />
    )}
  </NavLink>
);

export const Sidebar = ({ mobileOpen, onCloseMobile }) => {
  const { user, logout } = useAuth();
  const { prefs, patch } = usePrefs();
  const nav = useNavigate();
  const [pinned, setPinned] = useState([]);
  const [navCounts, setNavCounts] = useState({ unread: 0, overdue: 0, approvals_pending: 0 });
  const { unhealthy: syncUnhealthy, consecutiveFailures } = useSyncHealth();
  const collapsed = !!prefs.sidebar_collapsed;

  useEffect(() => {
    if (!user) return;
    api.get('/prefs/pinned').then(r => setPinned(r.data || [])).catch(() => setPinned([]));
  }, [user?.id, (prefs.pinned_page_ids || []).join(',')]);

  // All useFeature hooks at top-level BEFORE any early return — rules-of-hooks compliant.
  const fKra = useFeature('kra');
  const fTimesheets = useFeature('timesheets');
  const fRhythmV4 = useFeature('operating_rhythm_v4');
  const fProjects = useFeature('projects');
  const fWorkOrders = useFeature('work_orders');
  const fQuotes = useFeature('quotes');
  const fVisits = useFeature('visits');
  const fKnowledge = useFeature('knowledge_hub');
  const fTemplates = useFeature('templates');
  const fSettings = useFeature('settings');
  const fDriveSetup = useFeature('drive_setup');
  const fReports = useFeature('reports');
  const fGateDelegation = useFeature('gate_delegation');
  const fTeamDirectory = useFeature('team_directory');
  const fLearningHub = useFeature('learning_hub');

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const loadCounts = async () => {
      const [n, c, a] = await Promise.all([
        api.get('/notifications/count').catch(() => ({ data: {} })),
        fRhythmV4 ? api.get('/commitments/me?window=all').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        user.role === 'owner' ? api.get('/approvals/summary').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      ]);
      if (!alive) return;
      setNavCounts({
        unread: n.data?.unread || 0,
        overdue: c.data?.counts?.overdue || 0,
        approvals_pending: a.data?.pending_count || 0,
      });
    };
    loadCounts();
    const t = setInterval(loadCounts, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.id, fRhythmV4]);

  if (!user) return null;
  const role = user.role;
  const toggleCollapsed = () => patch({ sidebar_collapsed: !collapsed });

  const content = (
    <div className={`h-screen border-r flex flex-col ${collapsed ? 'w-[68px]' : 'w-[276px]'} transition-all duration-200`}
         style={{ background: 'hsl(var(--surface-0))', borderColor: 'hsl(var(--border))' }}>
      <div className="px-3 py-3 flex items-center justify-between border-b" style={{ borderColor: 'hsl(var(--border))' }}>
        <Link to="/home" className="flex items-center gap-2 min-w-0">
          {collapsed ? (
            <div className="relative">
              <Logo variant="icon" surface="auto" height={32} testId="sidebar-logo-icon" />
              {syncUnhealthy && (
                <span
                  data-testid="sync-health-badge-collapsed"
                  title={`Live sync paused (${consecutiveFailures} failed check${consecutiveFailures === 1 ? '' : 's'})`}
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-[hsl(var(--surface-0))]"
                  style={{ background: 'hsl(var(--danger))' }}
                />
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                <Logo variant="icon" surface="auto" height={32} testId="sidebar-logo-icon" />
                {syncUnhealthy && (
                  <span
                    data-testid="sync-health-badge-dot"
                    title={`Live sync paused (${consecutiveFailures} failed check${consecutiveFailures === 1 ? '' : 's'})`}
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-[hsl(var(--surface-0))]"
                    style={{ background: 'hsl(var(--danger))' }}
                  />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-heading font-semibold text-[14px] leading-tight truncate flex items-center gap-1.5">
                  <span className="truncate">32DECEMBER OS</span>
                  {syncUnhealthy && (
                    <span
                      data-testid="sync-health-badge-text"
                      title={`Live sync paused — ${consecutiveFailures} failed poll${consecutiveFailures === 1 ? '' : 's'}`}
                      className="text-[10px] font-medium px-1.5 py-[1px] rounded-full text-white"
                      style={{ background: 'hsl(var(--danger))' }}
                    >
                      sync paused
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[hsl(var(--foreground-subtle))] truncate">Internal Operating Platform</div>
              </div>
            </>
          )}
        </Link>
        <div className="flex items-center gap-1">
          <button data-testid="sidebar-collapse-button"
                  className="hidden lg:inline-flex p-1 rounded hover:bg-[hsl(var(--surface-2))]"
                  onClick={toggleCollapsed} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
          <button className="lg:hidden p-1 rounded hover:bg-[hsl(var(--surface-2))]" onClick={onCloseMobile}>
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto side-scroll px-2 py-3 space-y-0.5" data-testid="sidebar-nav">
        {!collapsed && <div className="side-section-label">Workspace</div>}
        <NavItem to="/home" icon={Home} label="Home" testId="sidebar-nav-home" collapsed={collapsed} />
        {['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture', 'team'].includes(role) && (
          <NavItem to="/design-desk" icon={CheckSquare} label="Design Desk" testId="sidebar-nav-design-desk" collapsed={collapsed} />
        )}
        {fRhythmV4 && <NavItem to="/my-week" icon={CalendarDays} label="My Week" testId="sidebar-nav-my-week" collapsed={collapsed} badge={navCounts.overdue} />}
        {fRhythmV4 && <NavItem to="/commitments" icon={Target} label="Commitments" testId="sidebar-nav-commitments" collapsed={collapsed} />}
        {fKra && <NavItem to="/kra" icon={Target} label="My KRAs" testId="sidebar-nav-kra" collapsed={collapsed} />}
        {fTimesheets && <NavItem to="/timesheets" icon={Clock} label="Timesheets" testId="sidebar-nav-timesheets" collapsed={collapsed} />}
        {fProjects && <NavItem to="/projects" icon={Layers} label="Projects" testId="sidebar-nav-projects" collapsed={collapsed} />}
        {fProjects && <NavItem to="/master-projects" icon={Database} label="Master Project Data" testId="sidebar-nav-master-projects" collapsed={collapsed} />}
        <NavItem to="/followups" icon={ClipboardCheck} label="Follow-ups" testId="sidebar-nav-followups" collapsed={collapsed} />
        {fTeamDirectory && <NavItem to="/team" icon={Users} label="Team" testId="sidebar-nav-team" collapsed={collapsed} />}
        {fWorkOrders && canSeeWOs(role) && <NavItem to="/work-orders" icon={Inbox} label="Work Orders" testId="sidebar-nav-work-orders" collapsed={collapsed} />}
        {fQuotes && canSeeQuotes(role) && <NavItem to="/quotes" icon={ClipboardList} label="Quote Register" testId="sidebar-nav-quotes" collapsed={collapsed} />}
        {fVisits && canSeeVisits(role) && <NavItem to="/visits" icon={Plane} label="Travel & Visits" testId="sidebar-nav-visits" collapsed={collapsed} />}
        {canSeePendingInvoices(role) && <NavItem to="/pending-invoices" icon={ReceiptText} label="Pending Invoices" testId="sidebar-nav-pending-invoices" collapsed={collapsed} />}
        {fReports && <NavItem to="/reports" icon={BarChart3} label="Reports" testId="sidebar-nav-reports" collapsed={collapsed} />}
        <NavItem to="/my-work" icon={Briefcase} label="My Work" testId="sidebar-nav-my-work" collapsed={collapsed} />
        {isOwner(role) && <NavItem to="/reports/data-quality" icon={ShieldAlert} label="Data Quality" testId="sidebar-nav-data-quality" collapsed={collapsed} />}
        {['owner', 'operations', 'accounts'].includes(role) && <NavItem to="/archives" icon={Archive} label="Archived Records" testId="sidebar-nav-archives" collapsed={collapsed} />}

        {!collapsed && <div className="side-section-label">Knowledge</div>}
        {fKnowledge && <NavItem to="/knowledge" icon={BookOpen} label="Knowledge Hub" testId="sidebar-nav-knowledge" collapsed={collapsed} />}
        {fLearningHub && <NavItem to="/guide" icon={GraduationCap} label="How to use" testId="sidebar-nav-guide" collapsed={collapsed} />}
        {fTemplates && <NavItem to="/templates" icon={FileText} label="Templates" testId="sidebar-nav-templates" collapsed={collapsed} />}
        {fTemplates && <NavItem to="/emails" icon={Mail} label="Email Templates" testId="sidebar-nav-emails" collapsed={collapsed} />}
        <NavItem to="/wizard" icon={Compass} label="What next? Wizard" testId="sidebar-nav-wizard" collapsed={collapsed} />

        {pinned.length > 0 && !collapsed && (
          <>
            <div className="side-section-label flex items-center gap-1"><Star size={10} /><span>Pinned</span></div>
            {pinned.map(p => (
              <NavLink key={p.id} to={`/knowledge/${p.id}`} data-testid={`sidebar-pinned-${p.id}`}
                       className={({ isActive }) => `side-nav-item ${isActive ? 'active' : ''}`}>
                <span className="text-[14px] leading-none w-4 text-center">{p.icon || p.metadata?.emoji || '📌'}</span>
                <span className="truncate">{p.title}</span>
              </NavLink>
            ))}
          </>
        )}

        {!collapsed && <div className="side-section-label">Alerts</div>}
        <NavItem to="/notifications" icon={Bell} label="Notifications" testId="sidebar-nav-notifications" collapsed={collapsed} badge={navCounts.unread} />
        {isOwner(role) && <NavItem to="/approvals" icon={ShieldCheck} label="Approvals" testId="sidebar-nav-approvals" collapsed={collapsed} badge={navCounts.approvals_pending} />}
        {(role === 'owner' || role === 'operations') && <NavItem to="/outbox" icon={Mail} label="Outbox" testId="sidebar-nav-outbox" collapsed={collapsed} />}

        {(isOwner(role) || canSeeAudit(role)) && (
          <>
            {!collapsed && <div className="side-section-label">Admin</div>}
            {isOwner(role) && <NavItem to="/admin/users" icon={Users} label="Users" testId="sidebar-nav-admin-users" collapsed={collapsed} />}
            {isOwner(role) && <NavItem to="/admin/settings" icon={Wrench} label="Admin Settings" testId="sidebar-nav-admin-settings" collapsed={collapsed} />}
            {isOwner(role) && <NavItem to="/admin/access" icon={ShieldCheck} label="Access & Permissions" testId="sidebar-nav-access" collapsed={collapsed} />}
            {isOwner(role) && <NavItem to="/admin/wo-review" icon={LinkIcon} label="WO Review" testId="sidebar-nav-wo-review" collapsed={collapsed} />}
            {isOwner(role) && <NavItem to="/admin/holidays" icon={CalendarDays} label="Holiday Calendar" testId="sidebar-nav-admin-holidays" collapsed={collapsed} />}
            {isOwner(role) && <NavItem to="/admin/release-manager" icon={ClipboardList} label="Release Manager" testId="sidebar-nav-release" collapsed={collapsed} />}
            {isOwner(role) && fDriveSetup && <NavItem to="/admin/drive-setup" icon={FolderTree} label="Drive Setup Wizard" testId="sidebar-nav-drive-setup" collapsed={collapsed} />}
            {fGateDelegation && (role === 'owner' || role === 'operations') && <NavItem to="/admin/gate-delegations" icon={ShieldAlert} label="Gate Delegations" testId="sidebar-nav-gate-delegations" collapsed={collapsed} />}
            {(role === 'owner' || role === 'operations' || role === 'dm_creative' || role === 'dm_technical' || role === 'dm_architecture') && <NavItem to="/admin/sop-needs-confirmation" icon={AlertTriangle} label="SOPs: Needs confirmation" testId="sidebar-nav-sop-needs-confirmation" collapsed={collapsed} />}
            {(role === 'owner' || role === 'operations' || role === 'hr') && <NavItem to="/admin/wor-archive" icon={Archive} label="WOR Archive" testId="sidebar-nav-wor-archive" collapsed={collapsed} />}
            {canSeeAudit(role) && <NavItem to="/admin/audit" icon={ShieldAlert} label="Audit Log" testId="sidebar-nav-admin-audit" collapsed={collapsed} />}
            {isOwner(role) && <NavItem to="/admin/feedback" icon={MessageSquare} label="Team Feedback" testId="sidebar-nav-admin-feedback" collapsed={collapsed} />}
          </>
        )}

        <div className="pt-2" />
        {fSettings && <NavItem to="/settings" icon={Settings} label="Settings" testId="sidebar-nav-settings" collapsed={collapsed} />}
      </div>

      <div className="border-t p-3 flex items-center gap-2" style={{ borderColor: 'hsl(var(--border))' }} data-testid="sidebar-user-block">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-[13px] flex-none shadow-sm"
             style={{ background: user.avatar_color || 'hsl(var(--primary))' }}>
          {user.name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium truncate flex items-center gap-1">
              {user.name}
              {prefs.status_emoji && <span title={prefs.status_text || ''}>{prefs.status_emoji}</span>}
            </div>
            <div className="flex items-center gap-1 mt-0.5"><RoleBadge role={role} /></div>
            {prefs.status_text && <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] truncate mt-0.5">{prefs.status_text}</div>}
          </div>
        )}
        {!collapsed && (
          <button data-testid="sidebar-logout" onClick={() => { logout(); nav('/login'); }}
                  className="p-1.5 rounded hover:bg-[hsl(var(--surface-2))]" title="Sign out">
            <LogOut size={15} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block flex-none">{content}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="bg-black/40 flex-1" onClick={onCloseMobile} />
          <div className="flex-none">{content}</div>
        </div>
      )}
    </>
  );
};
